import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Radio, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Match, MatchStatus } from "@/types/api.types";

interface MatchViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  match: Match | null;
}

const STATUS_COLORS: Record<MatchStatus, string> = {
  SCHEDULED: "bg-gray-100 text-gray-700",
  LIVE: "bg-green-100 text-green-700",
  COMPLETED: "bg-blue-100 text-blue-700",
  POSTPONED: "bg-yellow-100 text-yellow-700",
  CANCELLED: "bg-red-100 text-red-700",
};

const formatPhase = (phase: string) =>
  phase.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function MatchView({
  open,
  onOpenChange,
  match,
}: MatchViewProps) {
  const navigate = useNavigate();

  if (!match) return null;

  const kickoff = new Date(match.kickoffTime);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle>Match Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Scoreboard */}
          <div className="bg-gray-50 rounded-xl p-6">
            <div className="flex items-center justify-between">
              {/* Home Nation */}
              <div className="flex flex-col items-center gap-2 flex-1">
                {match.homeNation.flagUrl && (
                  <img
                    src={match.homeNation.flagUrl}
                    alt={match.homeNation.name}
                    className="w-16 h-10 object-cover rounded border"
                  />
                )}
                <p className="font-bold text-lg text-center">
                  {match.homeNation.name}
                </p>
                <code className="text-xs text-gray-500">
                  {match.homeNation.code}
                </code>
              </div>

              {/* Score */}
              <div className="flex flex-col items-center gap-1 px-6">
                {match.status === "SCHEDULED" ? (
                  <p className="text-3xl font-bold text-gray-400">vs</p>
                ) : (
                  <p className="text-4xl font-bold">
                    {match.homeScore} - {match.awayScore}
                  </p>
                )}
                <div className="flex items-center gap-1">
                  {match.status === "LIVE" && (
                    <Radio className="h-3 w-3 text-green-600" />
                  )}
                  <Badge className={STATUS_COLORS[match.status]}>
                    {match.status}
                  </Badge>
                </div>
              </div>

              {/* Away Nation */}
              <div className="flex flex-col items-center gap-2 flex-1">
                {match.awayNation.flagUrl && (
                  <img
                    src={match.awayNation.flagUrl}
                    alt={match.awayNation.name}
                    className="w-16 h-10 object-cover rounded border"
                  />
                )}
                <p className="font-bold text-lg text-center">
                  {match.awayNation.name}
                </p>
                <code className="text-xs text-gray-500">
                  {match.awayNation.code}
                </code>
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4 border-t pt-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Phase</p>
              <Badge variant="outline" className="mt-1">
                {formatPhase(match.phase)}
              </Badge>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Stage</p>
              <p className="text-sm font-medium mt-1">{match.stage || "—"}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Kickoff Time</p>
              <p className="text-sm font-medium mt-1">
                {kickoff.toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
              <p className="text-sm text-gray-500">
                {kickoff.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Venue</p>
              <p className="text-sm font-medium mt-1">{match.venue || "—"}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Total Events</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {match._count?.events || 0}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">
                Match Finished
              </p>
              <p className="text-sm font-medium mt-1">
                {match.isFinished ? "✅ Yes" : "⏳ No"}
              </p>
            </div>

            {match.endTime && (
              <div>
                <p className="text-sm font-medium text-gray-500">End Time</p>
                <p className="text-sm font-medium mt-1">
                  {new Date(match.endTime).toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Live warning */}
          {match.status === "LIVE" && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Radio className="h-4 w-4 text-green-600 animate-pulse" />
                <p className="text-sm text-green-800 font-medium">
                  This match is currently live.
                </p>
              </div>
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => { onOpenChange(false); navigate("/live-scorer"); }}
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Open Live Scorer
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
